import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, IconButton, List, ListItem, Paper } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import {
    orderTagsTemplate,
    addOrderTagTemplate,
    modifyOrderTagTemplate,
    deleteOrderTagTemplate,
    reorderOrderTagsTemplate,
} from 'apollo/templates/order-tags';
import { useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrderTag } from 'models/tags';
import AddUpdateOrderTagDialog from './add-update-order-tag-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';

const ManageTags: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);
    const [currentTag, setCurrentTag] = useState<OrderTag | undefined>();
    const [assignTagDeleteSnackbarOpen, setAssignTagDeleteSnackbarOpen] = useState(false);
    const [tagOrderSavedKey, setTagOrderSavedKey] = useState(0);
    const [tags, setTags] = useState<OrderTag[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: { orderTags } = {},
        loading,
        refetch,
    } = useQuery(orderTagsTemplate, {
        notifyOnNetworkStatusChange: true,
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (orderTags) setTags(orderTags);
    }, [orderTags]);

    const [addTag] = useMutation(addOrderTagTemplate, {
        onCompleted: () => refetch(),
        onError: (error) => showBoundary(error),
    });

    const [modifyTag] = useMutation(modifyOrderTagTemplate, {
        onCompleted: () => refetch(),
        onError: (error) => showBoundary(error),
    });

    const [deleteTag] = useMutation(deleteOrderTagTemplate, {
        onCompleted: ({ deleteOrderTag }) => {
            if (!deleteOrderTag) {
                setAssignTagDeleteSnackbarOpen(true);
                return;
            }
            refetch();
        },
        onError: (error) => showBoundary(error),
    });

    const [reorderTags] = useMutation(reorderOrderTagsTemplate, {
        onCompleted: () => setTagOrderSavedKey((key) => key + 1),
        onError: (error) => showBoundary(error),
    });

    const handleDeleteTagClick = (tagId: string) => {
        setAssignTagDeleteSnackbarOpen(false);
        deleteTag({ variables: { tagId } });
    };

    const handleAddTagClick = () => {
        setCurrentTag(undefined);
        setIsAddEditOpen(true);
    };

    const handleEditTagClick = (tag: OrderTag) => {
        setCurrentTag(tag);
        setIsAddEditOpen(true);
    };

    const handleAddTag = (tag: OrderTag) => {
        addTag({ variables: tag });
        handleCloseAddEdit();
    };

    const handleUpdateTag = (tag: OrderTag) => {
        modifyTag({ variables: { ...tag, tagId: tag._id } });
        handleCloseAddEdit();
    };

    const handleCloseAddEdit = () => {
        setIsAddEditOpen(false);
        setCurrentTag(undefined);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.source.index === result.destination.index) return;
        const next = [...tags];
        const [moved] = next.splice(result.source.index, 1);
        next.splice(result.destination.index, 0, moved);
        setTags(next);
        reorderTags({ variables: { tagIds: next.map((tag) => tag._id) } });
    };

    return (
        <Fragment>
            {tags.length > 0 && (
                <Paper variant="outlined">
                    {!loading && (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="order-tags">
                                {(provided) => (
                                    <List dense ref={provided.innerRef} {...provided.droppableProps}>
                                        {tags.map((tag, index) => (
                                            <Draggable key={tag._id} draggableId={tag._id as string} index={index}>
                                                {(dragProvided, dragSnapshot) => (
                                                    <ListItem
                                                        ref={dragProvided.innerRef}
                                                        {...dragProvided.draggableProps}
                                                        secondaryAction={
                                                            <Box display="flex" gap={0.5}>
                                                                <IconButton
                                                                    onClick={() => handleEditTagClick(tag)}
                                                                    edge="end"
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                                <IconButton
                                                                    onClick={() =>
                                                                        handleDeleteTagClick(tag._id as string)
                                                                    }
                                                                    edge="end"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Box>
                                                        }
                                                        sx={{
                                                            background: dragSnapshot.isDragging
                                                                ? 'action.hover'
                                                                : undefined,
                                                        }}
                                                    >
                                                        <Box
                                                            {...dragProvided.dragHandleProps}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                mr: 1,
                                                                cursor: 'grab',
                                                                color: dragSnapshot.isDragging
                                                                    ? 'primary.main'
                                                                    : 'action.active',
                                                            }}
                                                        >
                                                            <DragIndicatorIcon />
                                                        </Box>
                                                        <Chip
                                                            size="medium"
                                                            label={tag.name}
                                                            sx={{
                                                                backgroundColor: tag.color,
                                                                color: '#ffffff',
                                                                minWidth: '20%',
                                                            }}
                                                        />
                                                    </ListItem>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </List>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </Paper>
            )}
            <Box mt={1} mb={3}>
                <Button onClick={() => handleAddTagClick()} startIcon={<AddIcon />}>
                    {translate('lab-settings:ADD_TAG')}
                </Button>
            </Box>
            <AddUpdateOrderTagDialog
                open={isAddEditOpen}
                tag={currentTag}
                onAdd={handleAddTag}
                onUpdate={handleUpdateTag}
                onDialogClose={handleCloseAddEdit}
            />
            {assignTagDeleteSnackbarOpen ? (
                <AlertSnackbar type="error" message={translate('lab-settings:ASSIGN_TAG_DELETE_INFO')} />
            ) : undefined}
            {tagOrderSavedKey > 0 ? (
                <AlertSnackbar
                    key={tagOrderSavedKey}
                    type="success"
                    message={translate('lab-settings:TAG_ORDER_SAVED')}
                />
            ) : undefined}
        </Fragment>
    );
};

export default ManageTags;
